import { useState, useEffect, useCallback } from 'react';
import { getKeywordSuggestions } from '../utils/keywordsApi';

interface UseKeywordSuggestionsResult {
  suggestions: string[];
  isLoading: boolean;
  error: string | null;
  refreshSuggestions: () => Promise<void>;
}

interface UseKeywordSuggestionsOptions {
  /**
   * Controls whether the hook fires the underlying `/api/keywords/suggestions`
   * request. Defaults to `true` for backwards compatibility with existing
   * callers (the wizard's `CatalogKeywordsTab`).
   *
   * Set to `false` to defer the fetch — useful when the typeahead lives
   * inside a collapsed accordion or other deferred-render UI. The backend
   * endpoint scans every keyword row owned by the user, so eager fetching
   * for hidden UI is an O(catalog-items) cost paid per page load.
   *
   * Flipping `enabled` from `false` → `true` triggers the fetch lazily.
   * `refreshSuggestions` still works regardless of `enabled` so callers
   * can force a refresh after a save.
   */
  enabled?: boolean;
}

/**
 * Hook to provide keyword autocomplete suggestions from the user's catalog items.
 *
 * When ownerId is provided, fetches the catalog owner's keyword suggestions so
 * that a delegate sees the owner's previously used keywords (fixes #952).
 */
export const useKeywordSuggestions = (
  ownerId?: string,
  options: UseKeywordSuggestionsOptions = {},
): UseKeywordSuggestionsResult => {
  const { enabled = true } = options;
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchSuggestions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getKeywordSuggestions(ownerId);
      setSuggestions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load keyword suggestions');
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    if (!enabled) return;
    fetchSuggestions();
  }, [enabled, fetchSuggestions]);

  return {
    suggestions,
    isLoading,
    error,
    refreshSuggestions: fetchSuggestions,
  };
};
